"use client";

import { useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Check } from "lucide-react";
import { EmojiSelect, Select } from "@/components/ui";
import {
  getPublisherGameTypeSelectOptions,
  type PublisherGameType,
} from "../config/gameTypes";
import { createSearchParamsUpdater } from "../utils/updateSearchParams";
import { useIsWindows } from "@/hooks";

interface PublisherGamesFiltersProps {
  type: PublisherGameType;
  comingSoon: boolean;
}

export function PublisherGamesFilters({
  type,
  comingSoon,
}: PublisherGamesFiltersProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const isWindows = useIsWindows();
  const updateParams = createSearchParamsUpdater(searchParams, pathname);

  const handleComingSoonChange = () => {
    const url = updateParams.update("coming_soon", comingSoon ? null : "true");
    startTransition(() => {
      router.push(url);
    });
  };

  const handleTypeChange = (nextType: string) => {
    const url = updateParams.update(
      "type",
      nextType === "all" ? null : nextType
    );
    startTransition(() => {
      router.push(url);
    });
  };

  return (
    <div className="inline-flex items-center rounded-md border border-[#2a475e] bg-[#16202d] shadow-sm overflow-visible h-10 transition-all hover:border-[#3a556e]">
      {/* Coming Soon Checkbox */}
      <div className="flex items-center gap-2.5 px-3 sm:px-4 py-2 hover:bg-[#1f2e3f]/50 transition-colors">
        <button
          type="button"
          role="checkbox"
          aria-checked={comingSoon}
          onClick={handleComingSoonChange}
          disabled={isPending}
          className={`
            relative flex items-center justify-center flex-shrink-0
            w-4 h-4 sm:w-5 sm:h-5 rounded border transition-all
            ${
              comingSoon
                ? "bg-[#66c0f4] border-[#66c0f4] shadow-sm"
                : "bg-[#1f2e3f] border-[#3a556e] hover:border-[#66c0f4]"
            }
            disabled:opacity-40 disabled:cursor-not-allowed
            focus:outline-none focus:ring-2 focus:ring-[#66c0f4] focus:ring-offset-1 focus:ring-offset-[#16202d]
          `}
        >
          {comingSoon && (
            <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#0e1419] stroke-[2.5]" />
          )}
        </button>
        <span className="text-xs sm:text-sm text-[#c7d5e0] select-none whitespace-nowrap font-medium">
          Coming Soon
        </span>
      </div>

      {/* Divider */}
      <div className="h-5 sm:h-6 w-px bg-[#2a475e]" aria-hidden="true" />

      {/* Type Select */}
      <div className="flex items-center gap-3 px-3 sm:px-4 py-2">
        <span className="hidden sm:inline text-xs sm:text-sm text-[#8f98a0] whitespace-nowrap leading-none">
          Type
        </span>
        <div className="flex-1 min-w-0 relative group">
          <div className="absolute inset-0 rounded -mx-1 bg-[#1f2e3f]/0 group-hover:bg-[#1f2e3f]/50 transition-colors pointer-events-none" />
          {isWindows ? (
            <EmojiSelect
              value={type}
              onChange={handleTypeChange}
              options={getPublisherGameTypeSelectOptions()}
              isLoading={isPending}
              className="relative z-10 !h-full text-xs min-w-[140px] sm:text-sm sm:min-w-[180px] !border-0 !bg-transparent !rounded-none !py-0 focus:!ring-0 cursor-pointer leading-none"
              ariaLabel="Game Type"
            />
          ) : (
            <Select
              value={type}
              onChange={handleTypeChange}
              options={getPublisherGameTypeSelectOptions()}
              isLoading={isPending}
              className="relative z-10 h-full text-xs min-w-[140px] sm:text-sm sm:min-w-[180px] !border-0 !bg-transparent !rounded-none !py-0 focus:!ring-0 cursor-pointer leading-none"
              ariaLabel="Game Type"
            />
          )}
        </div>
      </div>
    </div>
  );
}
